import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { loginRequest, isDevAuthBypass } from '@auth/index';

/**
 * Login - ClariCommand sign-in wall. Internal Entra-only (@claridynamics.com, enforced at the API).
 * The MSAL logic is unchanged from the kept spine; only the presentation moves to the editorial paper
 * design system.
 *
 * Deep links survive the wall (William 2026-07-14, pop-out fix): AuthRoute stashes the intended
 * path in router state; once authenticated we navigate THERE, not to '/'. An interactive
 * loginRedirect threads the same path through MSAL as redirectStartPage, so even a full Entra
 * round-trip returns to it. Before this, a pop-out /event/<id> that hit the wall (the popup's
 * copied MSAL cache hydrates a beat after first paint) bounced through /login and landed on the
 * whole portal at '/'.
 */
export function Login() {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const idle = params.get('reason') === 'idle';

  // Internal app paths only - anything else (or nothing) falls back to home.
  const rawFrom = (location.state as { from?: string } | null)?.from;
  const from = rawFrom && rawFrom.startsWith('/') && !rawFrom.startsWith('//') ? rawFrom : '/';

  useEffect(() => {
    if (isAuthenticated || isDevAuthBypass()) navigate(from, { replace: true });
  }, [isAuthenticated, navigate, from]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--paper)',
        padding: 24,
      }}
    >
      <div className="card" style={{ width: '100%', maxWidth: 420, textAlign: 'center', padding: 36 }}>
        <div className="section-eyebrow" style={{ justifyContent: 'center' }}>
          ClariDynamics
        </div>
        <h1 className="section-title" style={{ marginBottom: 6 }}>
          Member <em>Care</em>
        </h1>
        <p className="section-lede" style={{ margin: '0 auto 24px', maxWidth: 'none' }}>
          {idle
            ? 'You were signed out after inactivity to protect patient information. Sign in again to continue.'
            : 'Sign in with your ClariDynamics account to reach the member-care console.'}
        </p>
        <button
          type="button"
          className="btn btn-primary"
          style={{ width: '100%', justifyContent: 'center', padding: '11px 16px' }}
          onClick={() => void instance.loginRedirect({ ...loginRequest, redirectStartPage: from })}
        >
          Sign in
        </button>
      </div>
    </div>
  );
}
